import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang, imgUrl } from "@/i18n";

export default function Gallery() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ caption: "", category: "training", image_path: "" });
  const [filter, setFilter] = useState("all");
  const isStaff = ["admin", "staff"].includes(user?.role);

  const load = async () => {
    const r = await api.get("/gallery");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error(t("image")); return; }
    try {
      await api.post("/gallery", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ caption: "", category: "training", image_path: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const filtered = items.filter((g) => filter === "all" || g.category === filter);

  return (
    <div className="space-y-6" data-testid="gallery-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("gallery")}</h1>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="gallery-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              <SelectItem value="training">{t("training")}</SelectItem>
              <SelectItem value="match">{t("match")}</SelectItem>
            </SelectContent>
          </Select>
          {isStaff && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-photo-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                  <Plus size={16} className="mr-1" /> {t("addPhoto")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("addPhoto")}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>{t("image")} *</Label>
                    <Input data-testid="gallery-image" type="file" accept="image/*" required className="mt-1"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const r = await api.post("/upload", fd);
                          setForm((p) => ({ ...p, image_path: r.data.path }));
                          toast.success(t("saved"));
                        } catch { toast.error(t("error")); }
                      }} />
                  </div>
                  <div>
                    <Label>{t("caption")}</Label>
                    <Input data-testid="gallery-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>{t("type")}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="gallery-category" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="training">{t("training")}</SelectItem>
                        <SelectItem value="match">{t("match")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="gallery-submit-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("save")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {filtered.map((g) => (
          <figure key={g.photo_id} className="gfa-card gfa-card-hover group relative overflow-hidden" data-testid={`gallery-item-${g.photo_id}`}>
            <img src={imgUrl(g.image_path)} alt={g.caption} className="aspect-square w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" />
            <figcaption className="flex items-center justify-between p-3">
              <span className="truncate text-xs text-slate-600">{g.caption || (g.category === "match" ? t("match") : t("training"))}</span>
              {user?.role === "admin" && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" data-testid={`delete-photo-${g.photo_id}`}
                  onClick={async () => { if (window.confirm(t("confirmDelete"))) { await api.delete(`/gallery/${g.photo_id}`); load(); } }}>
                  <Trash2 size={13} />
                </Button>
              )}
            </figcaption>
          </figure>
        ))}
        {filtered.length === 0 && <p className="text-slate-400">{t("noData")}</p>}
      </div>
    </div>
  );
}
